/**
 * The data panel's two ETF tabs (`docs/plans/etf-components.md`; the approved
 * design is `docs/mockups/etf-components.html`):
 *
 * - **Components**, on an ETF: what the fund holds — tiles, a treemap sized by
 *   weight, and a sortable table. Clicking a line charts it.
 * - **ETFs**, on a stock: which US ETFs hold it, by weight in the fund or by $
 *   held, and the leveraged and inverse funds in a list of their own.
 *
 * Both are fetched when the tab opens; the server caches for the day. Neither
 * names where the data comes from (owner, 2026-10-30).
 */
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent as KeyEvent, type ReactElement, type RefObject } from "react";

import { fetchEtfComponents, fetchEtfHolders, type EtfComponents, type EtfHolders, type EtfLine } from "../api/etf";
import { squarify } from "../lib/holdings-fund";
import {
  componentTiles, firstDir, leverageLabel, mapTiles, money, orderHolders, signedPct, sortLines, TREEMAP_MAX_TILES,
  type ComponentKey, type HolderOrder, type MapTile, type Sort,
} from "../lib/etf-view";

interface Props {
  symbol: string;
  onSymbol?: (symbol: string) => void;
}

type Load<T> = { state: "loading" } | { state: "none" } | { state: "ready"; data: T };

function useLoad<T>(symbol: string, fetcher: (s: string) => Promise<T | undefined>): Load<T> {
  const [load, setLoad] = useState<Load<T>>({ state: "loading" });
  useEffect(() => {
    let live = true;
    setLoad({ state: "loading" });
    void fetcher(symbol).then((r) => {
      if (live) setLoad(r ? { state: "ready", data: r } : { state: "none" });
    });
    return () => {
      live = false;
    };
  }, [symbol, fetcher]);
  return load;
}

// ── Components ──────────────────────────────────────────────────────────────

export function EtfComponentsView({ symbol, onSymbol }: Props): ReactElement {
  const load = useLoad(symbol, fetchEtfComponents);
  if (load.state === "loading") return <Note text="loading…" />;
  if (load.state === "none") return <Note text={`${symbol}'s components are not available right now.`} />;
  return <Components c={load.data} onSymbol={onSymbol} />;
}

function Components({ c, onSymbol }: { c: EtfComponents; onSymbol?: ((s: string) => void) | undefined }): ReactElement {
  const [showMap, setShowMap] = useState(true);
  const [colour, setColour] = useState<"change" | "sector">("change");
  const [sort, setSort] = useState<Sort<ComponentKey>>({ key: "weight", dir: -1 });
  if (c.lines.length === 0) return <Note text={`No components published for ${c.etf}.`} />;
  const t = componentTiles(c.lines);
  const rows = sortLines(c.lines, sort);
  const by = (key: ComponentKey) => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: firstDir(key) }));
  const head = (key: ComponentKey, label: string, right = false) => (
    <th style={{ ...TH, ...(right && R), ...(sort.key === key && { color: "var(--qf-accent)" }) }} onClick={() => by(key)}
        aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
      {label}{sort.key === key ? (sort.dir === -1 ? " ▾" : " ▴") : ""}
    </th>
  );
  return (
    <div style={SCROLL} data-testid="etf-components">
      <div style={ASOF}>Basket as of {c.asOf ?? c.fetchedOn}</div>
      <div style={TILES}>
        <Tile k="Holdings" v={String(t.holdings)} s={`${c.lines.filter((l) => l.inUniverse).length} charted`} />
        <Tile k="Top 10" v={`${t.top10.toFixed(1)}%`} s="of the fund" />
        <Tile k="Up / Down" v={<><span style={POS}>{t.up}</span> / <span style={NEG}>{t.down}</span></>} s="today" />
        <Tile k="Fund Day % (est.)" v={<span style={t.weightedChange == null ? undefined : t.weightedChange >= 0 ? POS : NEG}>{signedPct(t.weightedChange)}</span>} s="by weight" />
      </div>
      <div style={CONTROLS}>
        <Pill options={[[true, "Map + Table"], [false, "Table"]]} value={showMap} onChange={setShowMap} />
        {showMap && <Pill options={[["change", "Day %"], ["sector", "Sector"]]} value={colour} onChange={setColour} />}
      </div>
      {showMap && <Treemap tiles={mapTiles(c.lines)} colour={colour} onSymbol={onSymbol} />}
      <table style={TABLE}>
        <thead>
          <tr>
            <th style={{ ...TH, cursor: "default" }}>#</th>
            {head("symbol", "Symbol")}
            {head("weight", "Weight", true)}
            {head("close", "Last", true)}
            {head("change", "Day %", true)}
            {head("sector", "Sector")}
          </tr>
        </thead>
        <tbody>
          {rows.map((l, i) => <LineRow key={`${l.symbol ?? l.name}-${i}`} l={l} rank={c.lines.indexOf(l) + 1} onSymbol={onSymbol} />)}
        </tbody>
      </table>
      <div style={FOOT}>Weights as the fund publishes them. Greyed lines are part of the fund but not symbols we chart.</div>
    </div>
  );
}

function LineRow({ l, rank, onSymbol }: { l: EtfLine; rank: number; onSymbol?: ((s: string) => void) | undefined }): ReactElement {
  const go = l.inUniverse && l.symbol && onSymbol ? () => onSymbol(l.symbol!) : undefined;
  return (
    <tr onClick={go} title={go ? `${l.name ?? l.symbol} — chart it` : "Not a symbol we chart"}
        style={{ cursor: go ? "pointer" : "default", color: l.inUniverse ? undefined : "var(--qf-text-muted)" }}>
      <td style={{ ...TD, ...MUTED }}>{rank}</td>
      <td style={{ ...TD, fontFamily: MONO, fontWeight: 600, ...NM }} title={l.name ?? undefined}>{l.symbol ?? l.name ?? "—"}</td>
      <td style={{ ...TD, ...R }}>{l.weight == null ? "—" : `${l.weight.toFixed(2)}%`}</td>
      <td style={{ ...TD, ...R }}>{l.close == null ? "—" : l.close.toFixed(2)}</td>
      <td style={{ ...TD, ...R, ...(l.changePercentCc == null ? {} : l.changePercentCc >= 0 ? POS : NEG) }}>{signedPct(l.changePercentCc)}</td>
      <td style={{ ...TD, ...NM, ...MUTED, maxWidth: 130 }}>{l.sector ?? "—"}</td>
    </tr>
  );
}

function Treemap({ tiles, colour, onSymbol }: {
  tiles: MapTile[]; colour: "change" | "sector"; onSymbol?: ((s: string) => void) | undefined;
}): ReactElement {
  const [ref, W] = useWidth();
  const H = 240;
  const sectors = [...new Set(tiles.map((t) => t.sector).filter((s): s is string => s != null))];
  const fill = (t: MapTile): string =>
    t.other ? "var(--qf-surface-alt)" : colour === "change" ? changeColour(t.change) : SECTOR_FILLS[sectors.indexOf(t.sector ?? "") % SECTOR_FILLS.length] ?? NEUTRAL;
  return (
    <div>
      <div ref={ref} style={{ position: "relative", height: H, border: "1px solid var(--qf-border)", borderRadius: 4, overflow: "hidden" }} data-testid="etf-treemap">
        {W > 0 && squarify(tiles, { x: 0, y: 0, w: W, h: H }).map((c, i) => {
          const fs = Math.max(9, Math.min(22, Math.sqrt(c.box.w * c.box.h) / 4.4));
          const go = c.clickable && onSymbol ? () => onSymbol(c.symbol!) : undefined;
          return (
            <div key={`${c.label}-${i}`} title={`${c.label} · ${c.v.toFixed(2)}%${c.other ? "" : ` · ${signedPct(c.change)}`}`}
                 {...(go && {
                   role: "button", tabIndex: 0, onClick: go,
                   onKeyDown: (e: KeyEvent<HTMLDivElement>) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } },
                 })}
                 style={{ position: "absolute", left: c.box.x, top: c.box.y, width: c.box.w, height: c.box.h, display: "flex",
                          flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden",
                          border: "1px solid var(--qf-background)", color: c.other ? "var(--qf-text-muted)" : "#fff", fontFamily: MONO, fontWeight: 600, fontSize: fs,
                          cursor: go ? "pointer" : "default", background: fill(c) }}>
              {c.box.w > 38 && c.box.h > 18 ? c.label : ""}
              {c.box.w > 60 && c.box.h > 38 && (
                <small style={{ fontWeight: 400, fontSize: Math.max(9, fs * 0.5) }}>
                  {c.other || colour === "sector" ? `${c.v.toFixed(1)}%` : signedPct(c.change)}
                </small>
              )}
            </div>
          );
        })}
      </div>
      <div style={LEGEND}>
        {colour === "change"
          ? <><span><i style={{ ...SWATCH, background: changeColour(2.5) }} />up</span><span><i style={{ ...SWATCH, background: changeColour(-2.5) }} />down</span></>
          : sectors.map((s, i) => <span key={s}><i style={{ ...SWATCH, background: SECTOR_FILLS[i % SECTOR_FILLS.length] }} />{s}</span>)}
        {tiles.some((t) => t.other) && (
          <span><i style={{ ...SWATCH, background: "var(--qf-surface-alt)" }} />Other: past the largest {TREEMAP_MAX_TILES}, or unpublished</span>
        )}
        <span>area = weight</span>
      </div>
    </div>
  );
}

/** Green or red, deeper as the move grows; grey with no move to show. */
function changeColour(c: number | null): string {
  if (c == null) return NEUTRAL;
  const a = Math.min(1, 0.28 + Math.abs(c) / 3.5);
  return c >= 0 ? `rgba(38,166,154,${a})` : `rgba(239,83,80,${a})`;
}

// ── ETFs holding a stock ────────────────────────────────────────────────────

/** Rows before "Show all". */
const HOLDER_ROWS = 25;

export function EtfHoldersView({ symbol, onSymbol }: Props): ReactElement {
  const load = useLoad(symbol, fetchEtfHolders);
  if (load.state === "loading") return <Note text="loading…" />;
  if (load.state === "none") return <Note text={`The ETFs holding ${symbol} are not available right now.`} />;
  return <Holders h={load.data} onSymbol={onSymbol} />;
}

function Holders({ h, onSymbol }: { h: EtfHolders; onSymbol?: ((s: string) => void) | undefined }): ReactElement {
  // The leveraged funds are the switch's third choice rather than a list under
  // the main one; a stock held only by them opens there.
  const [by, setBy] = useState<HolderOrder | "leveraged">(h.funds.length === 0 ? "leveraged" : "weight");
  const [all, setAll] = useState(false);
  if (h.funds.length === 0 && h.leveraged.length === 0) return <Note text={`No US ETF holds ${h.symbol}.`} />;
  const choices: [HolderOrder | "leveraged", string][] = [["weight", "By weight in fund"], ["value", "By value held"]];
  if (h.leveraged.length > 0) choices.push(["leveraged", `Leveraged & inverse · ${h.leveraged.length}`]);
  const rows = by === "leveraged" ? [] : orderHolders(h.funds, by);
  const shown = all ? rows : rows.slice(0, HOLDER_ROWS);
  const topWeight = orderHolders(h.funds, "weight")[0];
  const topValue = orderHolders(h.funds, "value")[0];
  const go = (etf: string) => (onSymbol ? () => onSymbol(etf) : undefined);
  return (
    <div style={SCROLL} data-testid="etf-holders">
      <div style={ASOF}>Fetched as of {h.fetchedOn} · {h.funds.length} US ETFs of {h.raw.toLocaleString()} funds worldwide</div>
      <div style={{ ...TILES, gridTemplateColumns: "repeat(3, 1fr)" }}>
        <Tile k="US ETFs" v={String(h.funds.length)} s={`hold ${h.symbol}`} />
        <Tile k="Top weight" v={topWeight?.weight == null ? "—" : `${topWeight.weight.toFixed(1)}%`} s={topWeight?.etf ?? ""} />
        <Tile k="Largest holder" v={money(topValue?.value ?? null)} s={topValue?.etf ?? ""} />
      </div>
      <div style={CONTROLS}>
        <Pill options={choices} value={by} onChange={setBy} />
      </div>
      {by === "leveraged" ? (
        <>
          <table style={TABLE}>
            <thead>
              <tr><th style={TH}>Fund</th><th style={TH}>Name</th><th style={{ ...TH, ...R }}>Leverage</th><th style={{ ...TH, ...R }}>Value ▾</th></tr>
            </thead>
            <tbody>
              {h.leveraged.map((f) => (
                <tr key={f.etf} onClick={go(f.etf)} style={{ cursor: onSymbol ? "pointer" : "default" }} title={`${f.name ?? f.etf} — chart it`}>
                  <td style={{ ...TD, fontFamily: MONO, fontWeight: 600 }}>{f.etf}</td>
                  <td style={{ ...TD, ...NM, ...MUTED, maxWidth: 180 }}>{f.name ?? ""}</td>
                  <td style={{ ...TD, ...R, ...(f.direction === "long" ? POS : NEG) }}>{leverageLabel(f.multiple, f.direction)}</td>
                  <td style={{ ...TD, ...R }}>{money(f.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={FOOT}>
            Kept apart because their weight is leverage, not a share of the fund. Leverage is read from the fund's name.
          </div>
        </>
      ) : (<>
      <table style={TABLE}>
        <thead>
          <tr>
            <th style={TH}>Fund</th>
            <th style={TH}>Name</th>
            <th style={{ ...TH, ...R, ...(by === "weight" && { color: "var(--qf-accent)" }) }}>Weight{by === "weight" ? " ▾" : ""}</th>
            <th style={{ ...TH, ...R, ...(by === "value" && { color: "var(--qf-accent)" }) }}>Value{by === "value" ? " ▾" : ""}</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((f) => (
            <tr key={f.etf} onClick={go(f.etf)} style={{ cursor: onSymbol ? "pointer" : "default" }} title={`${f.name ?? f.etf} — chart it`}>
              <td style={{ ...TD, fontFamily: MONO, fontWeight: 600 }}>{f.etf}</td>
              <td style={{ ...TD, ...NM, ...MUTED, maxWidth: 180 }}>{f.name ?? ""}</td>
              <td style={{ ...TD, ...R }}>{f.weight == null ? "—" : `${f.weight.toFixed(2)}%`}</td>
              <td style={{ ...TD, ...R }}>{money(f.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > HOLDER_ROWS && (
        <button type="button" style={LINK} onClick={() => setAll((a) => !a)}>
          {all ? `Show top ${HOLDER_ROWS}` : `Show all ${rows.length}`}
        </button>
      )}
      <div style={FOOT}>
        {by === "weight"
          ? `Who is concentrated in ${h.symbol}: sector and thematic funds rise to the top.`
          : `Who owns the most ${h.symbol}: the index funds.`}
      </div>
      </>)}
    </div>
  );
}

// ── pieces ──────────────────────────────────────────────────────────────────

function Note({ text }: { text: string }): ReactElement {
  return <div style={{ ...SCROLL, ...MUTED }}>{text}</div>;
}

function Tile({ k, v, s }: { k: string; v: ReactElement | string; s: string }): ReactElement {
  return (
    <div style={TILE}>
      <div style={TILE_K}>{k}</div>
      <div style={TILE_V}>{v}</div>
      <div style={TILE_S}>{s}</div>
    </div>
  );
}

function Pill<T extends string | boolean>({ options, value, onChange }: {
  options: [T, string][]; value: T; onChange: (v: T) => void;
}): ReactElement {
  return (
    <div style={PILL}>
      {options.map(([v, label]) => (
        <button key={String(v)} type="button" aria-pressed={v === value} onClick={() => onChange(v)}
                style={{ ...PILL_BTN, ...(v === value && PILL_ON) }}>
          {label}
        </button>
      ))}
    </div>
  );
}

/** The host's width, kept current. 0 until measured — and in jsdom, which has no ResizeObserver. */
function useWidth(): [RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const MONO = "ui-monospace, 'IBM Plex Mono', Consolas, monospace";
const NEUTRAL = "#3a404c";
/** A fixed set, as the 13F treemap's: sector colours are not theme tokens. */
const SECTOR_FILLS = ["#5c7cfa", "#26a69a", "#ab47bc", "#ffa726", "#29b6f6", "#ec407a", "#9ccc65", "#8d6e63", "#78909c", "#ffca28", "#7e57c2", "#26c6da"];
const SCROLL: CSSProperties = { padding: "8px 10px", display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", height: "100%", color: "var(--qf-text)", fontSize: 13 };
const ASOF: CSSProperties = { fontFamily: MONO, fontSize: 12, color: "var(--qf-text-muted)" };
const MUTED: CSSProperties = { color: "var(--qf-text-muted)" };
const POS: CSSProperties = { color: "var(--qf-positive)" };
const NEG: CSSProperties = { color: "var(--qf-negative)" };
const R: CSSProperties = { textAlign: "right", fontFamily: MONO, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const NM: CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const TILES: CSSProperties = { flexShrink: 0, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 };
const TILE: CSSProperties = { background: "var(--qf-background)", border: "1px solid var(--qf-border)", borderRadius: 4, padding: "6px 7px", minWidth: 0 };
const TILE_K: CSSProperties = { fontFamily: MONO, fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--qf-text-muted)" };
const TILE_V: CSSProperties = { fontFamily: MONO, fontSize: 15, fontWeight: 600, fontVariantNumeric: "tabular-nums" };
const TILE_S: CSSProperties = { fontFamily: MONO, fontSize: 10.5, color: "var(--qf-text-muted)", ...NM };
const CONTROLS: CSSProperties = { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" };
const PILL: CSSProperties = { display: "flex", border: "1px solid var(--qf-border)", borderRadius: 4, overflow: "hidden" };
const PILL_BTN: CSSProperties = { padding: "2px 8px", fontFamily: MONO, fontSize: 11, color: "var(--qf-text-muted)", background: "none", border: 0, cursor: "pointer" };
const PILL_ON: CSSProperties = { background: "var(--qf-selection, var(--qf-surface-alt))", color: "var(--qf-accent)" };
const TABLE: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12.5, flexShrink: 0 };
const TH: CSSProperties = {
  fontFamily: MONO, fontSize: 10.5, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--qf-text-muted)", fontWeight: 500,
  textAlign: "left", padding: "4px 6px", borderBottom: "1px solid var(--qf-border)", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none",
};
const TD: CSSProperties = { padding: "3px 6px", borderBottom: "1px solid var(--qf-border)" };
const FOOT: CSSProperties = { fontFamily: MONO, fontSize: 11, color: "var(--qf-text-muted)" };
const LINK: CSSProperties = { alignSelf: "flex-start", fontFamily: MONO, fontSize: 11.5, color: "var(--qf-accent)", background: "none", border: 0, padding: 0, cursor: "pointer" };
const LEGEND: CSSProperties = { display: "flex", gap: 10, flexWrap: "wrap", fontFamily: MONO, fontSize: 10.5, color: "var(--qf-text-muted)", marginTop: 4 };
const SWATCH: CSSProperties = { display: "inline-block", width: 11, height: 8, borderRadius: 1, marginRight: 3, verticalAlign: -1 };
